import { AlertTriangle, Check, X } from 'lucide-react';

import { useGetIntakeQuery } from '@/services/api/intakeService';
import { IntakeSummary } from '@/services/types/intake-types';

import { MatterSectionCard } from './MatterSectionCard';

interface CaseAssessmentCardProps {
  intakeId?: string | null;
}

/**
 * Converts a comma-separated string into a trimmed list, removing empties.
 */
function splitFlags(value?: string | null): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

/**
 * Maps recommendation decision to a display-friendly recommendation title
 */
function getRecommendationTitle(decision?: string): string {
  if (!decision) return 'No recommendation';

  const lowerDecision = decision.toLowerCase();
  if (lowerDecision.includes('likely accept')) {
    return 'We recommend taking this case';
  }
  if (lowerDecision.includes('potential accept')) {
    return 'We recommend taking this case with caution';
  }
  if (lowerDecision.includes('uncertain')) {
    return 'This case requires further review';
  }
  if (lowerDecision.includes('potential decline')) {
    return 'We recommend declining this case with reservations';
  }
  if (lowerDecision.includes('likely decline')) {
    return 'We recommend declining this case';
  }

  return decision;
}

interface IndicatorRowProps {
  icon: React.ReactNode;
  label: string;
  items: string[];
}

const IndicatorRow = ({ icon, label, items }: IndicatorRowProps) => (
  <div className="flex items-center gap-4 py-1">
    <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-primary/10">
      {icon}
    </div>
    <div className="flex gap-2 items-center justify-start flex-row min-w-0">
      <p className="text-sm font-bold text-zinc-900">{label}</p>
      <p className="text-xs font-medium text-zinc-700 mt-0.5">
        {items.length > 0 ? items.join(', ') : '—'}
      </p>
    </div>
  </div>
);

interface CaseAssessmentContentProps {
  summary: IntakeSummary;
}

const CaseAssessmentContent = ({ summary }: CaseAssessmentContentProps) => {
  const positiveItems = splitFlags(summary.green_flags);
  const cautionItems = splitFlags(summary.yellow_flags);
  const warningItems = splitFlags(summary.red_flags);

  const recommendationTitle = getRecommendationTitle(
    summary.recommendation_decision,
  );

  return (
    <div className="space-y-4">
      {/* Indicators */}
      <div>
        <IndicatorRow
          icon={<Check className="h-3 w-3 text-primary" />}
          label="Positive Indicators"
          items={positiveItems}
        />
        <IndicatorRow
          icon={<AlertTriangle className="h-3 w-3 text-primary" />}
          label="Caution Indicators"
          items={cautionItems}
        />
        <IndicatorRow
          icon={<X className="h-3 w-3 text-primary" />}
          label="Warning Indicators"
          items={warningItems}
        />
      </div>

      {/* Recommendation Section */}
      {(summary.recommendation_decision || summary.recommendation_reason) && (
        <div className="pt-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">
            Recommendation
          </p>
          <h3 className="text-xs font-semibold text-zinc-900 mb-2">
            {recommendationTitle}
          </h3>
          {summary.recommendation_reason && (
            <p className="text-xs font-medium text-zinc-700 leading-relaxed">
              {summary.recommendation_reason}
            </p>
          )}
          {summary.recommendation_counterpoint && (
            <p className="text-xs font-medium text-zinc-700 italic mt-3 leading-relaxed">
              {summary.recommendation_counterpoint_heading ||
                'Why you might not take the case:'}{' '}
              {summary.recommendation_counterpoint}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export const CaseAssessmentCard = ({ intakeId }: CaseAssessmentCardProps) => {
  const { data: intake, isLoading } = useGetIntakeQuery(intakeId || '', {
    skip: !intakeId,
  });

  // Don't render if no intake ID or no summary data
  if (!intakeId) {
    return null;
  }

  const summary = intake?.summary;
  const hasAssessmentData =
    summary &&
    (summary.green_flags ||
      summary.yellow_flags ||
      summary.red_flags ||
      summary.recommendation_decision ||
      summary.recommendation_reason);

  if (!isLoading && !hasAssessmentData) {
    return null;
  }

  return (
    <MatterSectionCard title="Case Assessment" isLoading={isLoading}>
      {summary && <CaseAssessmentContent summary={summary} />}
    </MatterSectionCard>
  );
};
